import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";

import { BasicTooltip } from "../../code-editor/BasicTooltip";
import { cn } from "../../utils";

import type { ContextMenuItems } from "./types";
import { contextMenuIconVariants, contextMenuItemVariants } from "./variants";

interface ContextMenuItemProps<TData> {
  contextMenuItem: ContextMenuItems<TData>;
  data: TData;
}

export const ContextMenuItem = <TData,>({
  contextMenuItem,
  data,
}: ContextMenuItemProps<TData>) => {
  const {
    id,
    label,
    icon: Icon,
    className,
    getDisabled,
    getContent,
    preventCloseOnSelect,
    tooltipContent,
    onClick,
    isDivider,
    variant,
    hidden,
    showIf,
  } = contextMenuItem;

  if (hidden) {
    return null;
  }

  if (showIf && !showIf(data)) {
    return null;
  }

  if (isDivider) {
    return <div key={id} className="my-1 h-px bg-gray-200" />;
  }

  const isDisabled = getDisabled?.(data);
  const shouldPreventClose =
    typeof preventCloseOnSelect === "function" ? preventCloseOnSelect(data) : preventCloseOnSelect;

  if (getContent) {
    return (
      <ContextMenuPrimitive.Item
        className={cn(
          contextMenuItemVariants({ variant: variant || "primary" }),
          "cursor-default",
          isDisabled && "!cursor-not-allowed opacity-50 hover:!bg-transparent",
          className,
        )}
        key={id}
      >
        {getContent(data)}
      </ContextMenuPrimitive.Item>
    );
  }
  const tooltipMessage =
    typeof tooltipContent === "function" ? tooltipContent(data) : tooltipContent;

  return (
    <ContextMenuPrimitive.Item
      className={cn(
        contextMenuItemVariants({ variant: variant || "primary" }),
        isDisabled && "!cursor-not-allowed opacity-50 hover:!bg-transparent",
        className,
      )}
      key={id}
      onSelect={e => {
        if (!isDisabled) {
          if (shouldPreventClose) {
            e.preventDefault();
          }
          onClick?.(data);
        }
      }}
    >
      <BasicTooltip shouldShow={Boolean(isDisabled && tooltipMessage)} content={tooltipMessage}>
        <span className="flex w-full min-w-0 items-center text-sm">
          {Icon && (
            <span
              className={contextMenuIconVariants({
                variant: variant || "primary",
              })}
            >
              <Icon className="h-5 w-5" />
            </span>
          )}
          <span className="min-w-0 flex-1 whitespace-nowrap">{label}</span>
        </span>
      </BasicTooltip>
    </ContextMenuPrimitive.Item>
  );
};
